'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';

interface EmailCapture {
  id: string;
  email: string;
  created_at: string;
}

interface MarketerApplication {
  id: string;
  user_id: string;
  linkedin_url: string;
  company: string;
  experience: string;
  industries: string[];
  territory: string;
  network: string;
  opportunity_intent: string;
  status: string;
  admin_notes: string | null;
  rejection_reason: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  user_profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    country: string;
  };
}

type OpportunityStatus = 'pending' | 'accepted' | 'duplicate' | 'existing_opportunity' | 'needs_info' | 'rejected';

interface Opportunity {
  id: string;
  partner_id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_title: string | null;
  industry: string | null;
  country: string | null;
  description: string;
  estimated_app_count: string | null;
  notes: string | null;
  opp_status: OpportunityStatus;
  admin_notes: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  submitted_at: string;
  user_profiles?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

const ADMIN_SESSION_KEY = 'smarck_admin_auth';

function generateOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

// ---------------------------------------------------------------------------
// Admin Login Gate
// ---------------------------------------------------------------------------
function AdminLoginGate({ onAuth }: { onAuth: () => void }) {
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pendingOtpRef = useRef<string>('');
  const otpExpiryRef = useRef<number>(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendOtp = async () => {
    setSendingOtp(true);
    setOtpError('');
    const code = generateOTP();
    pendingOtpRef.current = code;
    otpExpiryRef.current = Date.now() + 10 * 60 * 1000;
    try {
      const supabase = createClient();
      const { error } = await supabase.functions.invoke('send-admin-otp', { body: { otp: code } });
      if (error) throw error;
      setResendCooldown(60);
    } catch {
      setOtpError('Failed to send OTP. Please try again.');
      pendingOtpRef.current = '';
    } finally {
      setSendingOtp(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    const adminPassword = process.env.NEXT_PUBLIC_ADMIN_PASSWORD;
    if (!adminPassword) { setPasswordError('Admin access is not configured.'); setPasswordLoading(false); return; }
    if (password !== adminPassword) { setPasswordError('Incorrect password. Access denied.'); setPasswordLoading(false); return; }
    setPasswordLoading(false);
    setStep('otp');
    await sendOtp();
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp]; next[index] = value.slice(-1); setOtp(next); setOtpError('');
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setOtp(pasted.split('')); otpRefs.current[5]?.focus(); }
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault(); setOtpLoading(true); setOtpError('');
    const entered = otp.join('');
    if (entered.length < 6) { setOtpError('Please enter the full 6-digit code.'); setOtpLoading(false); return; }
    if (Date.now() > otpExpiryRef.current) { setOtpError('OTP has expired. Please request a new one.'); setOtpLoading(false); return; }
    if (entered !== pendingOtpRef.current) { setOtpError('Incorrect code. Please try again.'); setOtpLoading(false); return; }
    sessionStorage.setItem(ADMIN_SESSION_KEY, 'true');
    onAuth();
    setOtpLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || sendingOtp) return;
    setOtp(['', '', '', '', '', '']); setOtpError('');
    await sendOtp();
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  };

  const cardStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.09)' };
  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    background: 'rgba(255,255,255,0.05)',
    border: hasError ? '1px solid rgba(255,107,107,0.5)' : '1px solid rgba(255,255,255,0.1)',
    color: '#E8EDF2',
  });

  return (
    <AppLayout>
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl p-8" style={cardStyle}>
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)' }}>
              {step === 'password' ? (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                  <rect x="5" y="11" width="16" height="12" rx="2.5" stroke="#FF6B6B" strokeWidth="1.6" fill="none"/>
                  <path d="M9 11V8a4 4 0 0 1 8 0v3" stroke="#FF6B6B" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="13" cy="17" r="1.5" fill="#FF6B6B"/>
                </svg>
              ) : (
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                  <rect x="3" y="7" width="20" height="14" rx="2.5" stroke="#FF6B6B" strokeWidth="1.6" fill="none"/>
                  <path d="M3 11l10 6 10-6" stroke="#FF6B6B" strokeWidth="1.6"/>
                </svg>
              )}
            </div>
          </div>
          <h1 className="text-xl font-bold text-center mb-1 tracking-tight" style={{ color: '#E8EDF2', fontFamily: 'DM Sans, sans-serif' }}>
            {step === 'password' ? 'Admin Access' : 'Verify Identity'}
          </h1>
          <p className="text-xs text-center mb-7" style={{ color: '#6B7A8D' }}>
            {step === 'password' ? 'Authorized team members only' : 'Enter the 6-digit code sent to your email'}
          </p>
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2].map((n) => (
              <React.Fragment key={n}>
                {n === 2 && <div className="w-8 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />}
                <div className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                  style={{
                    background: (n === 1 && step === 'password') || (n === 2 && step === 'otp') ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.04)',
                    border: (n === 1 && step === 'password') || (n === 2 && step === 'otp') ? '1px solid rgba(255,107,107,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: (n === 1 && step === 'password') || (n === 2 && step === 'otp') ? '#FF6B6B' : '#6B7A8D',
                  }}>
                  {n === 1 && step === 'otp' ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#FF6B6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : n}
                </div>
              </React.Fragment>
            ))}
          </div>

          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold tracking-widest mb-2" style={{ color: '#6B7A8D' }}>PASSWORD</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password" autoFocus required
                  className="w-full rounded-lg px-4 py-2.5 text-sm outline-none transition-colors"
                  style={inputStyle(!!passwordError)} />
              </div>
              {passwordError && <p className="text-xs font-medium" style={{ color: '#FF6B6B' }}>{passwordError}</p>}
              <button type="submit" disabled={passwordLoading || !password}
                className="w-full py-2.5 rounded-lg text-xs font-bold tracking-widest transition-all disabled:opacity-40"
                style={{ background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.35)', color: '#FF6B6B' }}>
                {passwordLoading ? 'VERIFYING…' : 'CONTINUE'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="flex flex-col gap-5">
              {sendingOtp && (
                <div className="flex items-center justify-center gap-2 py-1">
                  <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                    style={{ borderColor: 'rgba(255,107,107,0.3)', borderTopColor: '#FF6B6B' }} />
                  <span className="text-xs" style={{ color: '#6B7A8D' }}>Sending code…</span>
                </div>
              )}
              <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input key={i} ref={(el) => { otpRefs.current[i] = el; }} type="text" inputMode="numeric"
                    maxLength={1} value={digit} onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-10 h-12 rounded-lg text-center text-lg font-bold outline-none transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', border: otpError ? '1px solid rgba(255,107,107,0.5)' : digit ? '1px solid rgba(255,107,107,0.4)' : '1px solid rgba(255,255,255,0.1)', color: '#E8EDF2' }} />
                ))}
              </div>
              {otpError && <p className="text-xs font-medium text-center" style={{ color: '#FF6B6B' }}>{otpError}</p>}
              <button type="submit" disabled={otpLoading || otp.join('').length < 6 || sendingOtp}
                className="w-full py-2.5 rounded-lg text-xs font-bold tracking-widest transition-all disabled:opacity-40"
                style={{ background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.35)', color: '#FF6B6B' }}>
                {otpLoading ? 'VERIFYING…' : 'VERIFY & SIGN IN'}
              </button>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => { setStep('password'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                  className="text-xs transition-colors" style={{ color: '#6B7A8D' }}>← Back</button>
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0 || sendingOtp}
                  className="text-xs font-semibold transition-colors disabled:opacity-40"
                  style={{ color: resendCooldown > 0 ? '#6B7A8D' : '#FF6B6B' }}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Opportunity Status Config
// ---------------------------------------------------------------------------
const OPP_STATUS_CONFIG: Record<OpportunityStatus, { label: string; color: string; bg: string; border: string }> = {
  pending:              { label: 'Pending Review',       color: '#FFB347', bg: 'rgba(255,179,71,0.1)',   border: 'rgba(255,179,71,0.25)' },
  accepted:             { label: 'Accepted',             color: '#4ECDC4', bg: 'rgba(78,205,196,0.1)',   border: 'rgba(78,205,196,0.25)' },
  duplicate:            { label: 'Duplicate',            color: '#A78BFA', bg: 'rgba(167,139,250,0.1)',  border: 'rgba(167,139,250,0.25)' },
  existing_opportunity: { label: 'Existing Opportunity', color: '#60A5FA', bg: 'rgba(96,165,250,0.1)',   border: 'rgba(96,165,250,0.25)' },
  needs_info:           { label: 'Needs Info',           color: '#FBBF24', bg: 'rgba(251,191,36,0.1)',   border: 'rgba(251,191,36,0.25)' },
  rejected:             { label: 'Rejected',             color: '#FF6B6B', bg: 'rgba(255,107,107,0.1)',  border: 'rgba(255,107,107,0.25)' },
};

const SECTION4_CLASSIFICATIONS: { value: OpportunityStatus; label: string }[] = [
  { value: 'accepted',             label: 'Accepted' },
  { value: 'duplicate',            label: 'Duplicate' },
  { value: 'existing_opportunity', label: 'Existing Opportunity' },
  { value: 'needs_info',           label: 'Needs Info' },
  { value: 'rejected',             label: 'Rejected' },
];

// ---------------------------------------------------------------------------
// Admin Dashboard
// ---------------------------------------------------------------------------
function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [activeTab, setActiveTab] = useState<'emails' | 'applications' | 'opportunities'>('emails');
  const [records, setRecords] = useState<EmailCapture[]>([]);
  const [applications, setApplications] = useState<MarketerApplication[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(true);
  const [oppsLoading, setOppsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [oppsError, setOppsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedApp, setExpandedApp] = useState<string | null>(null);
  const [expandedOpp, setExpandedOpp] = useState<string | null>(null);
  const [updatingApp, setUpdatingApp] = useState<string | null>(null);
  const [rejectingAppId, setRejectingAppId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvingAppId, setApprovingAppId] = useState<string | null>(null);
  // Opportunity review state
  const [reviewingOppId, setReviewingOppId] = useState<string | null>(null);
  const [selectedClassification, setSelectedClassification] = useState<OpportunityStatus | ''>('');
  const [oppAdminNotes, setOppAdminNotes] = useState('');
  const [updatingOpp, setUpdatingOpp] = useState<string | null>(null);
  // Search state for all 3 tabs
  const [emailSearch, setEmailSearch] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [oppSearch, setOppSearch] = useState('');
  const PAGE_SIZE = 20;
  // Pagination state for applications and opportunities tabs
  const [appsPage, setAppsPage] = useState(1);
  const [oppsPage, setOppsPage] = useState(1);

  const fetchRecords = useCallback(async () => {
    setLoading(true); setError(null);
    const supabase = createClient();
    const { data, error: err } = await supabase
      .from('email_captures')
      .select('id, email, created_at')
      .eq('is_archived', false)
      .order('created_at', { ascending: false });
    if (err) { setError(err.message); } else {
      const seen = new Set<string>();
      const unique = (data || []).filter((row) => { if (seen.has(row.email)) return false; seen.add(row.email); return true; });
      setRecords(unique);
    }
    setLoading(false);
  }, []);

  const handleDeleteEmail = async (id: string, email: string) => {
    if (!confirm(`Remove "${email}" from the email list?`)) return;
    // Optimistically remove from UI immediately
    setRecords((prev) => prev.filter((r) => r.email !== email));
    const supabase = createClient();
    // Soft-delete: archive ALL rows with this email
    const { error } = await supabase
      .from('email_captures')
      .update({ is_archived: true })
      .eq('email', email)
      .eq('is_archived', false);
    if (error) {
      // Revert optimistic update on failure and show error
      alert(`Failed to remove email: ${error.message}`);
      fetchRecords(); // Re-fetch to restore correct state
    }
  };

  const fetchApplications = useCallback(async () => {
    setAppsLoading(true); setAppsError(null);
    const supabase = createClient();
    const { data, error: err } = await supabase
      .from('marketer_applications')
      .select('*, user_profiles(first_name, last_name, email, country)')
      .order('submitted_at', { ascending: false });
    if (err) { setAppsError(err.message); } else { setApplications(data || []); }
    setAppsLoading(false);
  }, []);

  const fetchOpportunities = useCallback(async () => {
    setOppsLoading(true); setOppsError(null);
    const supabase = createClient();
    const { data, error: err } = await supabase
      .from('opportunities')
      .select('*, user_profiles!partner_id(first_name, last_name, email)')
      .order('submitted_at', { ascending: false });
    if (err) { setOppsError(err.message); } else { setOpportunities(data || []); }
    setOppsLoading(false);
  }, []);

  useEffect(() => { fetchRecords(); fetchApplications(); fetchOpportunities(); }, [fetchRecords, fetchApplications, fetchOpportunities]);

  const handleUpdateStatus = async (appId: string, newStatus: 'approved' | 'rejected', reason?: string) => {
    setUpdatingApp(appId);
    const supabase = createClient();
    const updatePayload: Record<string, unknown> = { status: newStatus, reviewed_at: new Date().toISOString() };
    if (newStatus === 'rejected') {
      updatePayload.rejection_reason = reason && reason.trim() ? reason.trim() : null;
    }
    const { error } = await supabase.from('marketer_applications')
      .update(updatePayload)
      .eq('id', appId);
    if (error) {
      alert(`Failed to update status: ${error.message}`);
    } else {
      setApplications((prev) => prev.map((a) => a.id === appId ? { ...a, status: newStatus, rejection_reason: updatePayload.rejection_reason as string | null } : a));
      setRejectingAppId(null);
      setRejectionReason('');
    }
    setUpdatingApp(null);
  };

  const handleClassifyOpportunity = async (oppId: string) => {
    if (!selectedClassification) return;
    setUpdatingOpp(oppId);
    const supabase = createClient();
    const { error } = await supabase
      .from('opportunities')
      .update({
        opp_status: selectedClassification,
        admin_notes: oppAdminNotes.trim() || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', oppId);
    if (error) {
      alert(`Failed to classify opportunity: ${error.message}`);
    } else {
      setOpportunities((prev) =>
        prev.map((o) =>
          o.id === oppId
            ? { ...o, opp_status: selectedClassification as OpportunityStatus, admin_notes: oppAdminNotes.trim() || null, reviewed_at: new Date().toISOString() }
            : o
        )
      );
      setReviewingOppId(null);
      setSelectedClassification('');
      setOppAdminNotes('');
    }
    setUpdatingOpp(null);
  };

  const openReviewPanel = (opp: Opportunity) => {
    setReviewingOppId(opp.id);
    setSelectedClassification(opp.opp_status === 'pending' ? '' : opp.opp_status);
    setOppAdminNotes(opp.admin_notes || '');
  };

  const handleExportCSV = () => {
    const header = 'No,Email,Submitted At';
    const rows = records.map((row, idx) =>
      `${idx + 1},"${row.email}","${new Date(row.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}"`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `email-captures-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusStyle = (status: string) => {
    if (status === 'approved') return { color: '#4ECDC4', bg: 'rgba(78,205,196,0.1)', border: 'rgba(78,205,196,0.25)' };
    if (status === 'rejected') return { color: '#FF6B6B', bg: 'rgba(255,107,107,0.1)', border: 'rgba(255,107,107,0.25)' };
    return { color: '#FFB347', bg: 'rgba(255,179,71,0.1)', border: 'rgba(255,179,71,0.25)' };
  };

  const pendingOppsCount = opportunities.filter((o) => o.opp_status === 'pending').length;

  // Filtered lists based on search
  const filteredRecords = records.filter((r) =>
    emailSearch.trim() === '' || r.email.toLowerCase().includes(emailSearch.toLowerCase())
  );
  const filteredApplications = applications.filter((app) => {
    if (appSearch.trim() === '') return true;
    const q = appSearch.toLowerCase();
    const profile = app.user_profiles as any;
    return (
      (profile?.first_name || '').toLowerCase().includes(q) ||
      (profile?.last_name || '').toLowerCase().includes(q) ||
      (profile?.email || '').toLowerCase().includes(q) ||
      app.company.toLowerCase().includes(q) ||
      app.territory.toLowerCase().includes(q) ||
      app.status.toLowerCase().includes(q)
    );
  });
  const filteredOpportunities = opportunities.filter((opp) => {
    if (oppSearch.trim() === '') return true;
    const q = oppSearch.toLowerCase();
    const profile = opp.user_profiles as any;
    return (
      opp.company_name.toLowerCase().includes(q) ||
      opp.contact_name.toLowerCase().includes(q) ||
      opp.contact_email.toLowerCase().includes(q) ||
      (opp.industry || '').toLowerCase().includes(q) ||
      (opp.country || '').toLowerCase().includes(q) ||
      (profile?.first_name || '').toLowerCase().includes(q) ||
      (profile?.last_name || '').toLowerCase().includes(q) ||
      opp.opp_status.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredRecords.length / PAGE_SIZE);
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const appsTotalPages = Math.ceil(filteredApplications.length / PAGE_SIZE);
  const paginatedApplications = filteredApplications.slice((appsPage - 1) * PAGE_SIZE, appsPage * PAGE_SIZE);

  const oppsTotalPages = Math.ceil(filteredOpportunities.length / PAGE_SIZE);
  const paginatedOpportunities = filteredOpportunities.slice((oppsPage - 1) * PAGE_SIZE, oppsPage * PAGE_SIZE);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ color: '#E8EDF2' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: '#E8EDF2', fontFamily: 'DM Sans, sans-serif' }}>Admin Panel</h1>
            <p className="text-xs mt-0.5" style={{ color: '#6B7A8D' }}>SMARCK-M Management</p>
          </div>
          <button onClick={onLogout}
            className="flex items-center gap-2 text-xs font-semibold tracking-widest px-4 py-2 rounded-lg transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6B7A8D' }}>
            SIGN OUT
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl w-fit flex-wrap" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {([
            { key: 'emails', label: `Email Captures${!loading ? ` (${records.length})` : ''}` },
            { key: 'applications', label: `Marketer Applications${!appsLoading ? ` (${applications.length})` : ''}` },
            { key: 'opportunities', label: `Opportunities${!oppsLoading ? ` (${opportunities.length})` : ''}${pendingOppsCount > 0 ? ` · ${pendingOppsCount} pending` : ''}` },
          ] as const).map((tab) => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setCurrentPage(1); setAppsPage(1); setOppsPage(1); }}
              className="px-5 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: activeTab === tab.key ? 'rgba(255,107,107,0.15)' : 'transparent',
                color: activeTab === tab.key ? '#FF6B6B' : '#6B7A8D',
                border: activeTab === tab.key ? '1px solid rgba(255,107,107,0.3)' : '1px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Email Captures Tab */}
        {activeTab === 'emails' && (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between px-5 py-3 gap-3 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
              <span className="text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>EMAIL CAPTURES</span>
              <div className="flex items-center gap-3 flex-1 justify-end flex-wrap">
                <div className="relative">
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    value={emailSearch}
                    onChange={(e) => { setEmailSearch(e.target.value); setCurrentPage(1); }}
                    placeholder="Search emails…"
                    className="pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none w-48"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#E8EDF2' }}
                  />
                </div>
                {!loading && !error && records.length > 0 && (
                  <button onClick={handleExportCSV}
                    className="flex items-center gap-2 text-xs font-semibold tracking-widest px-3 py-1.5 rounded-lg transition-all"
                    style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B' }}>
                    EXPORT CSV
                  </button>
                )}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: 'rgba(255,107,107,0.3)', borderTopColor: '#FF6B6B' }} />
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>Failed to load</p>
                <p className="text-xs mb-4" style={{ color: '#6B7A8D' }}>{error}</p>
                <button onClick={fetchRecords} className="text-xs font-semibold px-4 py-2 rounded-lg"
                  style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', color: '#FF6B6B' }}>RETRY</button>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm" style={{ color: '#A8B2C1' }}>
                  {emailSearch.trim() ? `No emails match "${emailSearch}"` : 'No emails captured yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        <th className="text-left px-5 py-3 text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>#</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>EMAIL</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>SUBMITTED AT</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecords.map((row, idx) => {
                        const globalIdx = (currentPage - 1) * PAGE_SIZE + idx;
                        return (
                          <tr key={row.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                            <td className="px-5 py-3.5 text-xs" style={{ color: '#4B5563' }}>{globalIdx + 1}</td>
                            <td className="px-5 py-3.5 font-mono text-xs" style={{ color: '#E8EDF2' }}>{row.email}</td>
                            <td className="px-5 py-3.5 text-xs" style={{ color: '#A8B2C1' }}>
                              {new Date(row.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-5 py-3.5 text-xs text-right">
                              <button
                                onClick={() => handleDeleteEmail(row.id, row.email)}
                                className="flex items-center gap-1 text-xs font-semibold transition-all ml-auto px-2 py-1 rounded"
                                style={{ color: '#FF6B6B', background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)' }}
                                title="Remove email"
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                                </svg>
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredRecords.length > 0 && (
                  <div className="flex items-center justify-between px-5 py-3"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                    <p className="text-xs" style={{ color: '#6B7A8D' }}>
                      Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRecords.length)} of {filteredRecords.length}
                    </p>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                        className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>‹</button>
                      <span className="text-xs px-2" style={{ color: '#6B7A8D' }}>{currentPage} / {totalPages}</span>
                      <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                        className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Marketer Applications Tab */}
        {activeTab === 'applications' && (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between px-5 py-3 gap-3 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
              <span className="text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>MARKETER APPLICATIONS</span>
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  value={appSearch}
                  onChange={(e) => { setAppSearch(e.target.value); setAppsPage(1); }}
                  placeholder="Search applicants…"
                  className="pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none w-48"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#E8EDF2' }}
                />
              </div>
            </div>
            {appsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: 'rgba(255,107,107,0.3)', borderTopColor: '#FF6B6B' }} />
              </div>
            ) : appsError ? (
              <div className="text-center py-16">
                <p className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>Failed to load</p>
                <p className="text-xs mb-4" style={{ color: '#6B7A8D' }}>{appsError}</p>
                <button onClick={fetchApplications} className="text-xs font-semibold px-4 py-2 rounded-lg"
                  style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', color: '#FF6B6B' }}>RETRY</button>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm" style={{ color: '#A8B2C1' }}>
                  {appSearch.trim() ? `No applications match "${appSearch}"` : 'No marketer applications yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  {paginatedApplications.map((app) => {
                    const statusStyle = getStatusStyle(app.status);
                    const isExpanded = expandedApp === app.id;
                    const profile = app.user_profiles as any;
                    return (
                      <div key={app.id}>
                        <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                          onClick={() => setExpandedApp(isExpanded ? null : app.id)}>
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="flex-shrink-0">
                              <p className="text-sm font-semibold" style={{ color: '#E8EDF2' }}>
                                {profile?.first_name || ''} {profile?.last_name || ''}
                              </p>
                              <p className="text-xs" style={{ color: '#6B7A8D' }}>{profile?.email || app.user_id}</p>
                            </div>
                            <div className="hidden sm:block text-xs" style={{ color: '#A8B2C1' }}>{app.company}</div>
                            <div className="hidden md:block text-xs" style={{ color: '#A8B2C1' }}>{app.territory}</div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                              style={{ background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
                              {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                            </span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="2"
                              style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-5 pb-5" style={{ background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-4">
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>LinkedIn</p>
                                <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer"
                                  className="text-xs break-all" style={{ color: '#4ECDC4' }}>{app.linkedin_url}</a>
                              </div>
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Country</p>
                                <p className="text-xs" style={{ color: '#A8B2C1' }}>{profile?.country || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Industries</p>
                                <div className="flex flex-wrap gap-1">
                                  {(app.industries || []).map((ind) => (
                                    <span key={ind} className="text-xs px-2 py-0.5 rounded"
                                      style={{ background: 'rgba(78,205,196,0.1)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.2)' }}>{ind}</span>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Territory</p>
                                <p className="text-xs" style={{ color: '#A8B2C1' }}>{app.territory}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Experience</p>
                                <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{app.experience}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Network</p>
                                <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{app.network}</p>
                              </div>
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Opportunity Generation Intent</p>
                                <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{app.opportunity_intent}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Submitted</p>
                                <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                  {new Date(app.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            </div>

                            {app.status === 'pending' && (
                              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                {rejectingAppId === app.id ? (
                                  <div className="flex flex-col gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold tracking-widest mb-1.5" style={{ color: '#6B7A8D' }}>
                                        REJECTION REASON <span style={{ color: '#5A6478' }}>(optional)</span>
                                      </label>
                                      <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Briefly explain why the application was declined…"
                                        rows={3}
                                        className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none transition-colors"
                                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,107,107,0.25)', color: '#E8EDF2' }}
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <button onClick={() => handleUpdateStatus(app.id, 'rejected', rejectionReason)} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,107,107,0.15)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.35)' }}>
                                        {updatingApp === app.id ? '…' : '✗ Confirm Reject'}
                                      </button>
                                      <button onClick={() => { setRejectingAppId(null); setRejectionReason(''); }} disabled={updatingApp === app.id}
                                        className="px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7A8D', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : approvingAppId === app.id ? (
                                  <div className="flex flex-col gap-3">
                                    <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                      Are you sure you want to <span style={{ color: '#4ECDC4', fontWeight: 600 }}>approve</span> this application?
                                    </p>
                                    <div className="flex gap-2">
                                      <button onClick={() => { setApprovingAppId(null); handleUpdateStatus(app.id, 'approved'); }} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(78,205,196,0.15)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.35)' }}>
                                        {updatingApp === app.id ? '…' : '✓ Confirm Approve'}
                                      </button>
                                      <button onClick={() => setApprovingAppId(null)} disabled={updatingApp === app.id}
                                        className="px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7A8D', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex gap-3">
                                    {app.status !== 'approved' && (
                                      <button onClick={() => setApprovingAppId(app.id)} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(78,205,196,0.12)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.3)' }}>
                                        {updatingApp === app.id ? '…' : '✓ Approve'}
                                      </button>
                                    )}
                                    {app.status !== 'rejected' && (
                                      <button onClick={() => { setRejectingAppId(app.id); setRejectionReason(app.rejection_reason || ''); }} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,107,107,0.1)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.25)' }}>
                                        ✗ Reject
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {app.status !== 'pending' && (
                              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                {app.status === 'rejected' && app.rejection_reason && (
                                  <div className="mb-3 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(255,107,107,0.07)', border: '1px solid rgba(255,107,107,0.18)', color: '#A8B2C1' }}>
                                    <span className="font-semibold" style={{ color: '#FF6B6B' }}>Rejection reason: </span>{app.rejection_reason}
                                  </div>
                                )}
                                {rejectingAppId === app.id ? (
                                  <div className="flex flex-col gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold tracking-widest mb-1.5" style={{ color: '#6B7A8D' }}>
                                        REJECTION REASON <span style={{ color: '#5A6478' }}>(optional)</span>
                                      </label>
                                      <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Briefly explain why the application was declined…" rows={3}
                                        className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none transition-colors"
                                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,107,107,0.25)', color: '#E8EDF2' }} />
                                    </div>
                                    <div className="flex gap-2">
                                      <button onClick={() => handleUpdateStatus(app.id, 'rejected', rejectionReason)} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,107,107,0.15)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.35)' }}>
                                        {updatingApp === app.id ? '…' : '✗ Confirm Reject'}
                                      </button>
                                      <button onClick={() => { setRejectingAppId(null); setRejectionReason(''); }} disabled={updatingApp === app.id}
                                        className="px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7A8D', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : approvingAppId === app.id ? (
                                  <div className="flex flex-col gap-3">
                                    <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                      Are you sure you want to <span style={{ color: '#4ECDC4', fontWeight: 600 }}>approve</span> this application?
                                    </p>
                                    <div className="flex gap-2">
                                      <button onClick={() => { setApprovingAppId(null); handleUpdateStatus(app.id, 'approved'); }} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(78,205,196,0.15)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.35)' }}>
                                        {updatingApp === app.id ? '…' : '✓ Confirm Approve'}
                                      </button>
                                      <button onClick={() => setApprovingAppId(null)} disabled={updatingApp === app.id}
                                        className="px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7A8D', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex gap-3">
                                    {app.status !== 'approved' && (
                                      <button onClick={() => setApprovingAppId(app.id)} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(78,205,196,0.12)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.3)' }}>
                                        {updatingApp === app.id ? '…' : '✓ Approve'}
                                      </button>
                                    )}
                                    {app.status !== 'rejected' && (
                                      <button onClick={() => { setRejectingAppId(app.id); setRejectionReason(app.rejection_reason || ''); }} disabled={updatingApp === app.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                        style={{ background: 'rgba(255,107,107,0.1)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.25)' }}>
                                        ✗ Reject
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {filteredApplications.length > 0 && (
                  <div className="flex items-center justify-between px-5 py-3"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                    <p className="text-xs" style={{ color: '#6B7A8D' }}>
                      Showing {(appsPage - 1) * PAGE_SIZE + 1}–{Math.min(appsPage * PAGE_SIZE, filteredApplications.length)} of {filteredApplications.length}
                    </p>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setAppsPage((p) => Math.max(1, p - 1))} disabled={appsPage === 1}
                        className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>‹</button>
                      <span className="text-xs px-2" style={{ color: '#6B7A8D' }}>{appsPage} / {appsTotalPages}</span>
                      <button onClick={() => setAppsPage((p) => Math.min(appsTotalPages, p + 1))} disabled={appsPage === appsTotalPages}
                        className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Opportunities Tab */}
        {activeTab === 'opportunities' && (
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between px-5 py-3 gap-3 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
              <div>
                <span className="text-xs font-semibold tracking-widest" style={{ color: '#6B7A8D' }}>PARTNER OPPORTUNITIES</span>
                <p className="text-xs mt-0.5" style={{ color: '#4B5563' }}>Section 4 — Opportunity Registration Review</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    value={oppSearch}
                    onChange={(e) => { setOppSearch(e.target.value); setOppsPage(1); }}
                    placeholder="Search opportunities…"
                    className="pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none w-52"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#E8EDF2' }}
                  />
                </div>
                {pendingOppsCount > 0 && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: 'rgba(255,179,71,0.1)', color: '#FFB347', border: '1px solid rgba(255,179,71,0.25)' }}>
                    {pendingOppsCount} pending
                  </span>
                )}
              </div>
            </div>

            {oppsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: 'rgba(255,107,107,0.3)', borderTopColor: '#FF6B6B' }} />
              </div>
            ) : oppsError ? (
              <div className="text-center py-16">
                <p className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>Failed to load</p>
                <p className="text-xs mb-4" style={{ color: '#6B7A8D' }}>{oppsError}</p>
                <button onClick={fetchOpportunities} className="text-xs font-semibold px-4 py-2 rounded-lg"
                  style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', color: '#FF6B6B' }}>RETRY</button>
              </div>
            ) : filteredOpportunities.length === 0 ? (
              <div className="text-center py-16">
                {oppSearch.trim() ? (
                  <p className="text-sm" style={{ color: '#A8B2C1' }}>No opportunities match &quot;{oppSearch}&quot;</p>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="1.5">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                    <p className="text-sm font-semibold mb-1" style={{ color: '#A8B2C1' }}>No opportunities registered yet</p>
                    <p className="text-xs" style={{ color: '#6B7A8D' }}>Approved partners can register opportunities from their dashboard</p>
                  </>
                )}
              </div>
            ) : (
              <>
              <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                {paginatedOpportunities.map((opp) => {
                  const statusCfg = OPP_STATUS_CONFIG[opp.opp_status];
                  const isExpanded = expandedOpp === opp.id;
                  const isReviewing = reviewingOppId === opp.id;
                  const profile = opp.user_profiles as any;

                  return (
                    <div key={opp.id}>
                      {/* Summary row */}
                      <div
                        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedOpp(isExpanded ? null : opp.id)}
                      >
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold truncate" style={{ color: '#E8EDF2' }}>{opp.company_name}</p>
                            <p className="text-xs truncate" style={{ color: '#6B7A8D' }}>
                              {opp.contact_name}{opp.contact_title ? ` · ${opp.contact_title}` : ''} · {opp.contact_email}
                            </p>
                          </div>
                          <div className="hidden sm:block text-xs flex-shrink-0" style={{ color: '#A8B2C1' }}>
                            {profile ? `${profile.first_name} ${profile.last_name}` : '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                            style={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}>
                            {statusCfg.label}
                          </span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7A8D" strokeWidth="2"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-5 pb-5" style={{ background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-4">
                            <div>
                              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Registered By (Partner)</p>
                              <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                {profile ? `${profile.first_name} ${profile.last_name}` : '—'}
                              </p>
                              <p className="text-xs font-mono" style={{ color: '#6B7A8D' }}>{profile?.email || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Submitted</p>
                              <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                {new Date(opp.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Contact</p>
                              <p className="text-xs" style={{ color: '#A8B2C1' }}>{opp.contact_name}{opp.contact_title ? ` — ${opp.contact_title}` : ''}</p>
                              <p className="text-xs font-mono" style={{ color: '#6B7A8D' }}>{opp.contact_email}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Industry / Country</p>
                              <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                {[opp.industry, opp.country].filter(Boolean).join(' · ') || '—'}
                              </p>
                            </div>
                            {opp.estimated_app_count && (
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Est. Application Count</p>
                                <p className="text-xs" style={{ color: '#A8B2C1' }}>{opp.estimated_app_count}</p>
                              </div>
                            )}
                            <div className="md:col-span-2">
                              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Description</p>
                              <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{opp.description}</p>
                            </div>
                            {opp.notes && (
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Partner Notes</p>
                                <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{opp.notes}</p>
                              </div>
                            )}
                            {opp.admin_notes && !isReviewing && (
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Admin Notes</p>
                                <p className="text-xs leading-relaxed px-3 py-2 rounded-lg"
                                  style={{ color: '#A8B2C1', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                  {opp.admin_notes}
                                </p>
                              </div>
                            )}
                            {opp.reviewed_at && !isReviewing && (
                              <div>
                                <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#5A6478' }}>Reviewed At</p>
                                <p className="text-xs" style={{ color: '#A8B2C1' }}>
                                  {new Date(opp.reviewed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Classification / Review Panel */}
                          <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                            {isReviewing ? (
                              <div className="flex flex-col gap-4">
                                <div>
                                  <label className="block text-xs font-semibold tracking-widest mb-2" style={{ color: '#6B7A8D' }}>
                                    SECTION 4 CLASSIFICATION <span style={{ color: '#FF6B6B' }}>*</span>
                                  </label>
                                  <div className="flex flex-wrap gap-2">
                                    {SECTION4_CLASSIFICATIONS.map((cls) => {
                                      const cfg = OPP_STATUS_CONFIG[cls.value];
                                      const isSelected = selectedClassification === cls.value;
                                      return (
                                        <button
                                          key={cls.value}
                                          type="button"
                                          onClick={() => setSelectedClassification(cls.value)}
                                          className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                                          style={{
                                            background: isSelected ? cfg.bg : 'rgba(255,255,255,0.04)',
                                            color: isSelected ? cfg.color : '#6B7A8D',
                                            border: isSelected ? `1px solid ${cfg.border}` : '1px solid rgba(255,255,255,0.08)',
                                          }}
                                        >
                                          {cls.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold tracking-widest mb-1.5" style={{ color: '#6B7A8D' }}>
                                    ADMIN NOTES <span style={{ color: '#5A6478' }}>(optional)</span>
                                  </label>
                                  <textarea
                                    value={oppAdminNotes}
                                    onChange={(e) => setOppAdminNotes(e.target.value)}
                                    placeholder="Internal notes about this opportunity…"
                                    rows={3}
                                    className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none transition-colors"
                                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#E8EDF2' }}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleClassifyOpportunity(opp.id)}
                                    disabled={!selectedClassification || updatingOpp === opp.id}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                                    style={{ background: 'rgba(78,205,196,0.15)', color: '#4ECDC4', border: '1px solid rgba(78,205,196,0.35)' }}>
                                    {updatingOpp === opp.id ? '…' : '✓ Save Classification'}
                                  </button>
                                  <button
                                    onClick={() => { setReviewingOppId(null); setSelectedClassification(''); setOppAdminNotes(''); }}
                                    disabled={updatingOpp === opp.id}
                                    className="px-4 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                    style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7A8D', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => openReviewPanel(opp)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                                style={{ background: 'rgba(255,107,107,0.1)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.25)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                                {opp.opp_status === 'pending' ? 'Classify Opportunity' : 'Update Classification'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {filteredOpportunities.length > 0 && (
                <div className="flex items-center justify-between px-5 py-3"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}>
                  <p className="text-xs" style={{ color: '#6B7A8D' }}>
                    Showing {(oppsPage - 1) * PAGE_SIZE + 1}–{Math.min(oppsPage * PAGE_SIZE, filteredOpportunities.length)} of {filteredOpportunities.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setOppsPage((p) => Math.max(1, p - 1))} disabled={oppsPage === 1}
                      className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>‹</button>
                    <span className="text-xs px-2" style={{ color: '#6B7A8D' }}>{oppsPage} / {oppsTotalPages}</span>
                    <button onClick={() => setOppsPage((p) => Math.min(oppsTotalPages, p + 1))} disabled={oppsPage === oppsTotalPages}
                      className="w-8 h-8 rounded flex items-center justify-center text-xs transition-all disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#A8B2C1' }}>›</button>
                  </div>
                </div>
              )}
            </>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdminPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem(ADMIN_SESSION_KEY);
    setAuthed(stored === 'true');
  }, []);

  const handleAuth = () => setAuthed(true);
  const handleLogout = () => { sessionStorage.removeItem(ADMIN_SESSION_KEY); setAuthed(false); };

  if (authed === null) return null;
  if (!authed) return <AdminLoginGate onAuth={handleAuth} />;
  return <AdminDashboard onLogout={handleLogout} />;
}
